import AuthService from '../oauth/services/AuthService';
import {
  setIsUnderMaintenance,
  setIsUnderMaintenanceMessage,
} from '../store/actions';
import {Store} from './../store';
import {uriGetBus, uriGetNetwork} from './endpoints';
import {apiQueryClient} from './queryClient';
import {recordApiFailure} from '../util/appInsights';

const tenantAlias = process.env.REACT_APP_DEFAULT_TENANT_ALIAS;
const maintenanceApi = process.env.REACT_APP_BASE_API_MAINTENANCE;

const configs = (info?: boolean, feature?: string) => {
  const authorizationHeaders: any = {};
  const state = Store.getState();
  const localStorageUser = JSON.parse(localStorage?.getItem('user') as string);

  const token =
    localStorage?.getItem('access_token') ||
    localStorageUser?.access_token ||
    state?.accessToken?.value;
  if (!!token) {
    authorizationHeaders.authorization = `Bearer ${token}`;
  }

  return {
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      X_MYADV_API_KEY: tenantAlias,
      ...authorizationHeaders,
      ...(info
        ? {
            advSource: 'WebPortal',
            advBu: state.selectedBu.value.buId,
            advMemId: state.selectedBu.value.memId,
          }
        : null),
      ...(feature ? {feature: feature} : null),
    },
  };
};

const getDefaultHeaders = (feature?: string) => {
  const state = Store.getState();
  return {
    ...configs().headers,
    advSource: 'WebPortal',
    advBu: state.selectedBu.value.buId || state?.tenant?.value?.buDefaultAlias,
    advMemId: state.selectedBu.value.memId,
    X_MYADV_API_KEY: tenantAlias,
    'Accept-Language':
      (state.user.value === null
        ? state.language.value
        : state.user.value.language || state.language?.value) ||
      state?.stringsDefault?.value?.pt?.messages?.default_language,
    ...(feature ? {feature: feature} : null),
  };
};

export const configsNoContentType = (feature?: string) => {
  const authorizationHeaders: any = {};
  const state = Store.getState();
  const localStorageUser = JSON.parse(localStorage?.getItem('user') as string);

  const token =
    localStorage?.getItem('access_token') ||
    localStorageUser?.access_token ||
    state?.accessToken?.value;
  if (!!token) {
    authorizationHeaders.authorization = `Bearer ${token}`;
  }

  return {
    headers: {
      advSource: 'WebPortal',
      advBu:
        state.selectedBu.value.buId || state?.tenant?.value?.buDefaultAlias,
      advMemId: state.selectedBu.value.memId,
      X_MYADV_API_KEY: tenantAlias,
      Accept: '*/*',
      'Accept-Language':
        (state.user.value === null
          ? state.language.value
          : state.user.value.language || state.language?.value) ||
        state?.stringsDefault?.value?.pt?.messages?.default_language,
      ...authorizationHeaders,
      ...(feature ? {feature: feature} : null),
    },
  };
};

export const configsAcceptLanguage = (feature?: string) => {
  const authorizationHeaders: any = {};
  const state = Store.getState();
  const localStorageUser = JSON.parse(localStorage?.getItem('user') as string);

  const token =
    localStorage?.getItem('access_token') ||
    localStorageUser?.access_token ||
    state?.accessToken?.value;
  if (!!token) {
    authorizationHeaders.authorization = `Bearer ${token}`;
  }

  return {
    headers: {
      advSource: 'WebPortal',
      advBu:
        state.selectedBu.value.buId || state?.tenant?.value?.buDefaultAlias,
      advMemId: state.selectedBu.value.memId,
      X_MYADV_API_KEY: tenantAlias,
      Accept: '*/*',
      ...authorizationHeaders,
      'Accept-Language':
        state.user.value === null
          ? state.language.value
          : state.user.value.language ||
            state.language?.value ||
            state?.stringsDefault?.value?.pt?.messages?.default_language,
      ...(feature ? {feature: feature} : null),
    },
  };
};

export const isValidSession = () => {
  const state = Store.getState();
  const localStorageUser = JSON.parse(localStorage.getItem('user') as string);

  const token =
    localStorage?.getItem('access_token') ||
    localStorageUser?.access_token ||
    state?.accessToken?.value;
  return !!token;
};

export const clearAuthToken = (setClearedFlag: boolean = false) => {
  if (localStorage.getItem('logged_in_by_url') === 'false') {
    if (isValidSession()) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('expires_in');
    } else {
      if (setClearedFlag) {
        localStorage.setItem('cleared', 'true');
        window.location.reload();
      }
    }
  }
};

const handleError = async (res: any, url?: string, method?: string) => {
  let status = 0;
  let errorData: any = null;
  let statusText = '';
  let requestUrl = url || '';

  if (res && typeof res === 'object') {
    status = typeof res.status === 'number' ? res.status : 0;
    statusText = res.statusText || '';
    if (!requestUrl && res.url) {
      requestUrl = res.url;
    }

    try {
      const contentType = res.headers && typeof res.headers.get === 'function' ? res.headers.get('content-type') || '' : '';
      if (contentType.includes('application/json') && typeof res.json === 'function') {
        errorData = await res.json();
      } else if (typeof res.text === 'function') {
        errorData = await res.text();
      } else if (typeof res.json === 'function') {
        errorData = await res.json();
      } else if (res.data) {
        errorData = res.data;
      } else if (res.message) {
        errorData = { message: res.message };
      }
    } catch (e) {
      errorData = { message: 'Erro ao ler resposta de erro' };
    }
  } else if (typeof res === 'string') {
    errorData = { message: res };
  }

  // Registar falha de API no buffer circular de diagnóstico
  try {
    recordApiFailure({
      url: requestUrl,
      status: status || 0,
      statusText: statusText,
      method: method || 'GET',
      errorMessage: typeof errorData === 'string' ? errorData : errorData?.message || undefined,
    });
  } catch (e) {
    // Silencia erros de telemetria
  }

  return {
    status: status,
    data: errorData,
  };
};

const shouldCacheGet = (url: string) => {
  // Add here the url's to cache
  const cacheableUrls = [
    '/services/common',
    '/backoffice/bus/services',
    //'/backoffice/tenants'
  ];

  return cacheableUrls.some((cacheableUrls) => url.includes(cacheableUrls));
};

const getCacheKey = (url: string) => {
  const state = Store.getState();

  return [
    'api-get',
    url,
    state.selectedBu.value.buId || state?.tenant?.value?.buDefaultAlias || null,
    state.selectedBu.value.memId || null,
    state.user.value === null
      ? state.language.value
      : state.user.value.language || state.language?.value || null,
  ];
};

export const rawGet = (
  url: string,
  isRetry = false,
  numberOfTries = 0,
  feature?: string,
) => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'GET', // *GET, POST, PUT, DELETE, etc.
      headers: getDefaultHeaders(feature),
    }).then(async (res: any) => {
      Store.dispatch(
        setIsUnderMaintenance(
          res.status === 508 && url?.includes(maintenanceApi || ''),
        ),
      );
      if (res.status < 400) {
        res.json().then((json: any) => {
          resolve(JSON.parse(JSON.stringify(json)));
        });
      } else if (url === uriGetBus()) {
        if (numberOfTries < 4) {
          rawGet(url, false, numberOfTries + 1, feature);
        } else {
          const as = new AuthService();
          as.logout();
        }
      } else {
        if (res.status === 401) {
          if (!isRetry) {
            rawGet(url, true);
          } else {
            clearAuthToken(true);
          }
        } else {
          reject(await handleError(res, url, 'GET'));
        }
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'GET'));
    });
  });
};

export const get = (
  url: string,
  isRetry = false,
  numberOfTries = 0,
  feature?: string,
) => {
  if (isRetry || !shouldCacheGet(url)) {
    return rawGet(url, isRetry, numberOfTries, feature);
  }

  return apiQueryClient.fetchQuery({
    queryKey: getCacheKey(url),
    queryFn: () => rawGet(url, false, numberOfTries, feature),
  });
};

export const getWithCustomHeaders = (
  url: string,
  customHeaders: any,
  isRetry = false,
  isRedeMedica = false,
  settingError?: {(error: boolean): void; (arg0: boolean): any} | undefined,
  feature?: string,
) =>
  new Promise((resolve, reject) => {
    const state = Store.getState();
    fetch(url, {
      method: 'GET', // *GET, POST, PUT, DELETE, etc.
      headers: customHeaders
        ? {
            ...customHeaders,
            advSource: 'WebPortal',
            advBu:
              state.selectedBu.value.buId ||
              state?.tenant?.value?.buDefaultAlias,
            advMemId: state.selectedBu.value.memId,
            X_MYADV_API_KEY: tenantAlias,
            'Accept-Language':
              (state.user.value === null
                ? state.language.value
                : state.user.value.language || state.language?.value) ||
              state?.stringsDefault?.value?.pt?.messages?.default_language,
            ...(feature ? {feature: feature} : null),
          }
        : getDefaultHeaders(),
    }).then(async (res: any) => {
      Store.dispatch(
        setIsUnderMaintenance(
          res.status === 508 && url?.includes(maintenanceApi || ''),
        ),
      );
      if (res.status < 400) {
        res.json().then((json: any) => {
          resolve(JSON.parse(JSON.stringify(json)));
        });
      } else if (res.status === 404 && url === uriGetNetwork()) {
        settingError?.(true);
      } else if (res.status === 401) {
        if (!isRetry) {
          getWithCustomHeaders(
            url,
            customHeaders,
            true,
            isRedeMedica,
            settingError,
          );
        } else {
          isRedeMedica
            ? settingError && settingError(true)
            : clearAuthToken(true);
        }
      } else {
        reject(await handleError(res, url, 'GET'));
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'GET'));
    });
  });

export const getBlob = (
  url: string,
  isRetry = false,
  info = false,
  feature?: string,
) =>
  new Promise((resolve, reject) => {
    fetch(url, {
      method: 'GET', // *GET, POST, PUT, DELETE, etc.
      ...configs(info, feature),
    }).then(async (res: any) => {
      Store.dispatch(
        setIsUnderMaintenance(
          res.status === 508 && url?.includes(maintenanceApi || ''),
        ),
      );
      if (res.status < 400) {
        res.blob().then((blob: any) => {
          resolve(blob);
        });
      } else if (res.status === 401) {
        if (!isRetry) {
          get(url, true);
        } else {
          clearAuthToken(true);
        }
      } else {
        reject(await handleError(res, url, 'GET'));
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'GET'));
    });
  });

export const put = (
  url: string,
  body: any,
  feature?: string,
  isRetry = false,
) =>
  new Promise((resolve, reject) => {
    const state = Store.getState();
    fetch(url, {
      method: 'PUT', // *GET, POST, PUT, DELETE, etc.
      headers: getDefaultHeaders(feature),
      body: JSON.stringify(body),
    }).then(async (res: any) => {
      Store.dispatch(
        setIsUnderMaintenance(
          res.status === 508 && url?.includes(maintenanceApi || ''),
        ),
      );
      if (res.status < 400) {
        res.json().then((json: any) => {
          resolve(JSON.parse(JSON.stringify(json)));
        });
      } else if (res.status === 401) {
        if (!isRetry) {
          put(url, body, feature, true);
        } else {
          clearAuthToken(true);
        }
      } else {
        reject(await handleError(res, url, 'PUT'));
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'PUT'));
    });
  });

export const patch = (
  url: string,
  body: any,
  feature?: string,
  isRetry = false,
) =>
  new Promise((resolve, reject) => {
    const state = Store.getState();
    fetch(url, {
      method: 'PATCH', // *GET, POST, PUT, DELETE, etc.
      headers: getDefaultHeaders(feature),
      body: JSON.stringify(body),
    }).then(async (res: any) => {
      Store.dispatch(
        setIsUnderMaintenance(
          res.status === 508 && url?.includes(maintenanceApi || ''),
        ),
      );
      if (res.status < 400) {
        res.json().then((json: any) => {
          resolve(JSON.parse(JSON.stringify(json)));
        });
      } else if (res.status === 401) {
        if (!isRetry) {
          patch(url, body, feature, true);
        } else {
          clearAuthToken(true);
        }
      } else {
        reject(await handleError(res, url, 'PATCH'));
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'PATCH'));
    });
  });

//TODO: Update call
export const post = (url: any, body: any, isRetry = false, feature?: string) =>
  new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
      headers: getDefaultHeaders(feature),
      body: JSON.parse(JSON.stringify(body)),
    }).then(async (res: any) => {
      Store.dispatch(
        setIsUnderMaintenance(
          res.status === 508 && url?.includes(maintenanceApi || ''),
        ),
      );
      if (res.status < 400) {
        res.json().then((json: any) => {
          resolve(JSON.parse(JSON.stringify(json)));
        });
      } else if (res.status === 401) {
        if (!isRetry) {
          post(url, body, true);
        } else {
          clearAuthToken(true);
        }
      } else {
        reject(await handleError(res, url, 'POST'));
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'POST'));
    });
  });

export const postWithoutBody = (url: any, isRetry = false, feature?: string) =>
  new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
      headers: getDefaultHeaders(feature),
    }).then(async (res: any) => {
      Store.dispatch(
        setIsUnderMaintenance(
          res.status === 508 && url?.includes(maintenanceApi || ''),
        ),
      );
      if (res.status < 400) {
        res.json().then((json: any) => {
          resolve(JSON.parse(JSON.stringify(json)));
        });
      } else if (res.status === 401) {
        if (!isRetry) {
          post(url, true);
        } else {
          clearAuthToken(true);
        }
      } else {
        reject(await handleError(res, url, 'POST'));
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'POST'));
    });
  });

export const postWithCustomHeaders = (
  url: any,
  body: any,
  headers = configsNoContentType(),
  isRetry = false,
  timeOut = '29',
) =>
  new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
      ...headers,
      body: body,
      //signal: AbortSignal.timeout(parseInt(timeOut, 10) * 1000)
    })
      .then(async (res: any) => {
        Store.dispatch(
          setIsUnderMaintenance(
            res.status === 508 && url?.includes(maintenanceApi || ''),
          ),
        );
        if (res.status < 400) {
          res.json().then((json: any) => {
            resolve(JSON.parse(JSON.stringify(json)));
          });
        } else if (res.status === 401) {
          if (!isRetry) {
            postWithCustomHeaders(url, body, headers, true);
          } else {
            clearAuthToken(true);
          }
        } else {
          if (res.status === 500) {
            reject(await handleError(res, url, 'POST'));
          } else if (res.status === 401) {
            if (!isRetry) {
              postWithCustomHeaders(url, body, headers, true);
            } else {
              clearAuthToken(true);
            }
          } else {
            reject(await handleError(res, url, 'POST'));
          }
        }
      })
      .catch(async (error) => {
        reject(await handleError(error, url, 'POST'));
      });
  });

//TODO: Update call
export const remove = (url: string, feature?: string, isRetry = false) =>
  new Promise((resolve, reject) => {
    const state = Store.getState();
    fetch(url, {
      method: 'DELETE', // *GET, POST, PUT, DELETE, etc.
      headers: getDefaultHeaders(feature),
    }).then(async (res) => {
      if (res.status < 400) {
        resolve(res);
      } else {
        if (res.status === 401) {
          if (!isRetry) {
            get(url, true);
          } else {
            clearAuthToken(true);
          }
        } else {
          reject(await handleError(res, url, 'DELETE'));
        }
      }
    }).catch(async (error) => {
      reject(await handleError(error, url, 'DELETE'));
    });
  });

const api = {
  get,
  getWithCustomHeaders,
  put,
  post,
  postWithCustomHeaders,
  remove,
};

export default api;
